import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { getPosBillByNumber, getPosBills, posError } from "../../../../services/PosApiServices";
import { printPosBill, useReceiptHeader } from "./posPrint";
import { amt, day, time, Empty, PageHead } from "./PosParts";

/** POS ▸ Invoice Re-Print: any saved POS bill or return, by date or number. */
const PosReprint = ({ branch, state }) => {
  const header = useReceiptHeader();
  const businessDay = state?.transactionDate ? dayjs(state.transactionDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
  const [from, setFrom] = useState(businessDay);
  const [to, setTo] = useState(businessDay);
  const [search, setSearch] = useState("");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  // follow the business date until the user picks other dates
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched) return;
    setFrom(businessDay);
    setTo(businessDay);
  }, [businessDay, touched]);

  const load = useCallback(async () => {
    if (!branch?.guid || !from || !to) return;
    setLoading(true);
    try {
      setBills(await getPosBills(branch.guid, from, to));
    } catch (error) {
      toast.error(posError(error, "Could not load the bills"));
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [branch, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? bills.filter(
          (b) =>
            `${b.entryNumber}`.toLowerCase().includes(q) ||
            `${b.customerName ?? ""}`.toLowerCase().includes(q) ||
            `${b.contactNumber ?? ""}`.toLowerCase().includes(q)
        )
      : bills;
    return [...list].sort((a, b) => dayjs(b.invDate).valueOf() - dayjs(a.invDate).valueOf());
  }, [bills, search]);

  // a number outside the loaded dates is fetched on Enter
  const findByNumber = async () => {
    const q = search.trim();
    if (!q || shown.length) return;
    try {
      const bill = await getPosBillByNumber(branch.guid, q);
      setBills((prev) => [bill, ...prev.filter((b) => b.guid !== bill.guid)]);
    } catch (error) {
      toast.error(posError(error, `There is no POS bill ${q}`));
    }
  };

  return (
    <>
      <PageHead title="Invoice Re-Print" subtitle="POS bills and returns, by transaction date">
        <div className="pm-search">
          <input
            type="date"
            className="form-control"
            value={from}
            max={to}
            onChange={(e) => {
              setTouched(true);
              setFrom(e.target.value);
            }}
            aria-label="From"
          />
          <input
            type="date"
            className="form-control"
            value={to}
            min={from}
            onChange={(e) => {
              setTouched(true);
              setTo(e.target.value);
            }}
            aria-label="To"
          />
          <input
            className="form-control"
            placeholder="Bill no, customer, mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && findByNumber()}
          />
        </div>
      </PageHead>

      <div className="pm-card">
        {loading ? (
          <Empty>Loading…</Empty>
        ) : shown.length ? (
          <div className="pm-table-wrap">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>No</th>
                  <th>Type</th>
                  <th>Counter</th>
                  <th>Customer</th>
                  <th className="num">Net</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.guid}>
                    <td>{day(b.transactionDate)}</td>
                    <td>{time(b.invDate)}</td>
                    <td>
                      <b>{b.entryNumber}</b>
                    </td>
                    <td>
                      {b.transType === 3 ? (
                        <span className="pm-pill pm-pill--warn">Return{b.refNo ? ` · ${b.refNo}` : ""}</span>
                      ) : (
                        <span className="pm-pill pm-pill--accent">Bill</span>
                      )}
                    </td>
                    <td>
                      {b.counterName} · shift {b.shiftNo ?? "—"}
                    </td>
                    <td>{b.customerName || "—"}</td>
                    <td className="num">{amt(b.netTotal)}</td>
                    <td className="num">
                      <button type="button" className="btn btn-sm btn-pm" onClick={() => printPosBill(b, header)}>
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            No POS bills match. {search.trim() ? "Press Enter to look the number up on any date." : ""}
          </Empty>
        )}
      </div>
    </>
  );
};

PosReprint.propTypes = {
  branch: PropTypes.object,
  state: PropTypes.object,
};

export default PosReprint;
